import { useContext, useMemo, useState } from "react";
import AuthContext from "../../../../context/authProvider";
import ToastContext from "../../../../context/toastProvider";
import { cashnator, decashnator, isNumeric } from "../../../utils/functions";
import { getCalculatedDataPartner, updateProductPartner } from "../../functions";
import Modal from "../../../utils/modal";
import Button from "../../../atoms/button";
import LoadingOverlay from "../../../atoms/loadingOverlay";
import { gradingTypes, gradingTypeSupportsAutograph, normalizeGradingType, priorityTypes } from "../../../utils/gradingTypes";


const PartnerEditModal = ({ handleClose, productData, stores, refresh }) => {

    const { user } = useContext(AuthContext);
    const { goodToast, badToast } = useContext(ToastContext);

    const [gradingType, setGradingType] = useState(normalizeGradingType(productData?.gradingType) || "");
    const [numberOfCards, setNumberOfCards] = useState(productData?.numberOfCards || "");
    const [priority, setPriority] = useState(productData?.priority || "");
    const [deadline, setDeadline] = useState(productData?.deadline || "");
    const [finalPrice, setFinalPrice] = useState(cashnator(productData?.finalPrice) || "R$0,00");
    const [cardsValue, setCardsValue] = useState(cashnator(productData?.cardsValue) || "R$0,00")
    const [insurance, setInsurance] = useState(cashnator(productData?.insurance));
    const [autographEvaluation, setAutographEvaluation] = useState(!!productData?.autographEvaluation);
    const [preLacreFormat, setPreLacreFormat] = useState(productData?.preLacreFormat || "with_subgrades");
    const [priceValidatedAfterChanges, setPriceValidatedAfterChanges] = useState(true);

    const [loading, setLoading] = useState(false);

    const sensitiveChanged = useMemo(() => {
        return normalizeGradingType(gradingType) !== normalizeGradingType(productData?.gradingType)
            || `${numberOfCards}` !== `${productData?.numberOfCards || ""}`
            || priority !== (productData?.priority || "")
            || decashnator(insurance) !== (productData?.insurance || 0)
            || autographEvaluation !== !!productData?.autographEvaluation;
    }, [autographEvaluation, gradingType, insurance, numberOfCards, priority, productData]);

    const handleNumberOfCards = (value) => {
        if (!value) {
            setNumberOfCards("");
            return null;
        }
        if (isNumeric(value)) {
            setNumberOfCards(value);
        }
    }

    const handleDeadline = (value) => {
        if (!value) {
            setDeadline("");
            return null;
        }
        if (isNumeric(value)) {
            setDeadline(value);
        }
    }

    const handleFinalPrice = (value) => {
        if (!value) {
            setFinalPrice("R$0,00");
            return null;
        }
        const newValue = decashnator(value);
        if (isNumeric(newValue)) {
            setFinalPrice(cashnator(newValue));
        }
    }

    const handleInsurance = (value) => {
        if (!value) {
            setInsurance("R$0,00");
            return null;
        }
        const newValue = decashnator(value);
        if (isNumeric(newValue)) {
            setInsurance(cashnator(newValue));
        }
    }

    const handleCardsValue = (value) => {
        if (!value) {
            setCardsValue("R$0,00");
            return null;
        }
        const newValue = decashnator(value);
        if (isNumeric(newValue)) {
            setCardsValue(cashnator(newValue));
        }
    }

    const markPricingDirty = () => {
        setPriceValidatedAfterChanges(false);
    }

    const handleRecalculate = async () => {
        setLoading(true);
        const data = await getCalculatedDataPartner(
            user?.token,
            gradingType,
            numberOfCards,
            priority,
            decashnator(insurance),
            gradingTypeSupportsAutograph(gradingType) && autographEvaluation
        );
        if (data?.type !== "success") {
            badToast("Erro ao recalcular preço e prazo");
            setLoading(false);
            return null;
        }
        setFinalPrice(cashnator(data?.data?.finalPrice));
        setDeadline(`${data?.data?.deadline || ""}`);
        setPriceValidatedAfterChanges(true);
        goodToast("Preço e prazo recalculados");
        setLoading(false);
    }

    const handleSubmit = async () => {
        if (sensitiveChanged && !priceValidatedAfterChanges) {
            badToast("Recalcule o preço/prazo antes de salvar alterações de tipo, autógrafo, quantidade, prioridade ou taxa de manuseio.");
            return null;
        }
        setLoading(true);
        let body = {
            gradingType: gradingType,
            numberOfCards: numberOfCards,
            priority: priority,
            deadline: deadline,
            finalPrice: decashnator(finalPrice),
            cardsValue: decashnator(cardsValue),
            insurance: decashnator(insurance),
            userEmail: user?.email,
            autographEvaluation: gradingTypeSupportsAutograph(gradingType) && autographEvaluation,
            ...(normalizeGradingType(gradingType) === "pre" ? { preLacreFormat } : {})
        }

        const data = await updateProductPartner(user?.token, body, productData?._id);
        if (data?.type !== "success") {
            badToast("Erro ao atualizar pedido");
            setLoading(false);
            return null;
        }
        goodToast("Pedido atualizado!");
        setLoading(false);
        refresh();
    }

    return (
        <Modal handleClose={handleClose}>
            <h2 className="text-center text-lg font-bold">Editar informações do pedido</h2>
            <div className="relative">
                <div className="flex gap-4 justify-evenly">
                    <div className="">
                        <div className="flex items-center gap-2">
                            <p className="my-4">Tipo:</p>
                            <select className="h-fit text-black text-center disabled:bg-[#00000000] disabled:text-white" value={gradingType} onChange={e => {
                                const next = e.target.value;
                                setGradingType(next);
                                if (!gradingTypeSupportsAutograph(next)) {
                                    setAutographEvaluation(false);
                                }
                                markPricingDirty();
                            }}>
                                {
                                    Object.keys(gradingTypes).map((o, i) => (
                                        <option key={i} value={o}>{gradingTypes[o]}</option>
                                    ))
                                }
                            </select>
                        </div>
                        {gradingTypeSupportsAutograph(gradingType) ? (
                            <div className="flex items-center gap-2 my-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={autographEvaluation} onChange={e => { setAutographEvaluation(e.target.checked); markPricingDirty(); }} className="accent-[#D4A017]" />
                                    <span>Avaliação de autógrafo</span>
                                </label>
                            </div>
                        ) : null}
                        {
                            normalizeGradingType(gradingType) === "pre" ?
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="my-2 m-0">Pré — lacre:</p>
                                    <select className="text-black text-center h-fit px-2 max-w-[280px]" value={preLacreFormat} onChange={e => setPreLacreFormat(e.target.value)}>
                                        <option value="with_subgrades">Com subgrades</option>
                                        <option value="without_subgrades">Sem subgrades</option>
                                    </select>
                                </div>
                                : null
                        }
                        <div className="flex items-center w-fit gap-2">
                            <p className="my-4">Nº Cartas: </p>
                            <input className="text-black text-center h-fit px-2 w-20 disabled:bg-[#00000000] disabled:text-white" type="text" value={numberOfCards} onChange={e => { handleNumberOfCards(e.target.value); markPricingDirty(); }} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Prioridade:</p>
                            <select className="text-black text-center disabled:bg-[#00000000] disabled:text-white" value={priority} onChange={e => { setPriority(e.target.value); markPricingDirty(); }}>
                                {
                                    Object.keys(priorityTypes).map((p, i) => (
                                        <option key={i} value={p}>{priorityTypes[p]}</option>
                                    ))
                                }
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Prazo: </p>
                            <input className="text-black text-center h-fit px-2 w-20 disabled:bg-[#00000000] disabled:text-white" type="text" value={deadline} onChange={e => handleDeadline(e.target.value)} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Entrega:</p>
                                <p>Presencial</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="mt-1 mb-2">Loja:</p>
                                <p>{productData?.store?.name}</p>
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Preço final: </p>
                            <input className="text-black text-center h-fit px-2 w-20 disabled:bg-[#00000000] disabled:text-white" type="text" value={finalPrice} onChange={e => handleFinalPrice(e.target.value)} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Valor itens: </p>
                            <input className="text-black text-center h-fit px-2 w-20 disabled:bg-[#00000000] disabled:text-white" type="text" value={cardsValue} onChange={e => handleCardsValue(e.target.value)} />
                        </div>
                        <div className="flex items-center gap-2">
                            <p className="my-4">Taxa de manuseio: </p>
                            <input className="text-black text-center h-fit px-2 w-20 disabled:bg-[#00000000] disabled:text-white" type="text" value={insurance} onChange={e => { handleInsurance(e.target.value); markPricingDirty(); }} />
                        </div>
                    </div>
                </div>
                {
                    sensitiveChanged && !priceValidatedAfterChanges ?
                        <p className="text-yellow-400 text-center my-2">Campos sensíveis foram alterados. Recalcule preço/prazo antes de salvar.</p>
                        : null
                }
                <div className="w-fit m-auto">
                    <Button classes="bg-[#d59106] hover:bg-orange-500" click={handleRecalculate} disabled={!sensitiveChanged}>Recalcular preço/prazo</Button>
                </div>
                <div className="flex gap-2 w-fit m-auto my-4">
                    <Button classes="w-32 bg-red-400 hover:bg-red-300" click={handleClose}>Cancelar</Button>
                    <Button classes="w-32 bg-green-400 hover:bg-green-300" click={handleSubmit} disabled={sensitiveChanged && !priceValidatedAfterChanges}>Salvar</Button>
                </div>
                <LoadingOverlay loading={loading} />
            </div>
        </Modal>
    )
}

export default PartnerEditModal;
